import { createContext, useContext, useEffect, useState } from "react";
import api from "../lib/api";

export const BRAND_DEFAULTS = {
  colors: {
    gold: "#FFEC69",
    background: "#111111",
    surface: "#1A1A1A",
    surface_alt: "#0D0D0D",
    text: "#FFFFFF",
    muted: "#666666",
    border: "#866D4B",
    success: "#34D399",
    danger: "#FB7185",
  },
  images: {
    header_logo: { src: "/brand/flag.png", size: 100, opacity: 100, x: 0, y: 0, z: 10 },
    login_crest: { src: "/brand/crest.png", size: 100, opacity: 100, x: 0, y: 0, z: 10 },
    background: { src: "", enabled: false, size: 100, opacity: 12, x: 50, y: 50, z: 0 },
  },
};

function hexToHsl(hex) {
  let h = (hex || "#000000").replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let hh = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) hh = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) hh = (b - r) / d + 2;
    else hh = (r - g) / d + 4;
    hh *= 60;
  }
  return `${Math.round(hh)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function applyTheme(b) {
  const c = { ...BRAND_DEFAULTS.colors, ...(b?.colors || {}) };
  const root = document.documentElement;
  root.style.setProperty("--sn-gold", c.gold);
  root.style.setProperty("--sn-bg", c.background);
  root.style.setProperty("--sn-surface", c.surface);
  root.style.setProperty("--sn-surface-alt", c.surface_alt);
  root.style.setProperty("--sn-text", c.text);
  root.style.setProperty("--sn-muted", c.muted);
  root.style.setProperty("--sn-border", c.border);
  root.style.setProperty("--sn-success", c.success);
  root.style.setProperty("--sn-danger", c.danger);
  root.style.setProperty("--background", hexToHsl(c.background));
  root.style.setProperty("--foreground", hexToHsl(c.text));
  root.style.setProperty("--card", hexToHsl(c.surface));
  root.style.setProperty("--card-foreground", hexToHsl(c.text));
  root.style.setProperty("--popover", hexToHsl(c.surface));
  root.style.setProperty("--popover-foreground", hexToHsl(c.text));
  root.style.setProperty("--primary", hexToHsl(c.gold));
  root.style.setProperty("--primary-foreground", hexToHsl(c.background));
  root.style.setProperty("--accent", hexToHsl(c.gold));
  root.style.setProperty("--accent-foreground", hexToHsl(c.background));
  root.style.setProperty("--ring", hexToHsl(c.gold));
  root.style.setProperty("--border", hexToHsl(c.border));
  root.style.setProperty("--input", hexToHsl(c.border));
  root.style.setProperty("--muted-foreground", hexToHsl(c.muted));
  document.body.style.background = c.background;
  document.body.style.color = c.text;
}

const Ctx = createContext(null);

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(BRAND_DEFAULTS);

  useEffect(() => {
    api.get("/branding").then((r) => setBranding(r.data)).catch(() => {});
  }, []);

  useEffect(() => { applyTheme(branding); }, [branding]);

  return <Ctx.Provider value={{ branding, setBranding }}>{children}</Ctx.Provider>;
}

export const useBranding = () => useContext(Ctx);

export function imgStyle(img, baseSizePx) {
  return {
    width: "auto",
    height: `${Math.round(baseSizePx * (img.size ?? 100) / 100)}px`,
    opacity: (img.opacity ?? 100) / 100,
    transform: `translate(${img.x ?? 0}px, ${img.y ?? 0}px)`,
    position: "relative",
    zIndex: img.z ?? 10,
  };
}

export function BackgroundLayer() {
  const { branding } = useBranding();
  const bg = branding?.images?.background;
  if (!bg?.enabled || !bg.src) return null;
  return (
    <div
      data-testid="brand-background-layer"
      style={{
        position: "fixed", inset: 0, zIndex: bg.z ?? 0, pointerEvents: "none",
        backgroundImage: `url(${bg.src})`, backgroundRepeat: "no-repeat",
        backgroundSize: `${bg.size ?? 100}% auto`,
        backgroundPosition: `${bg.x ?? 50}% ${bg.y ?? 50}%`,
        opacity: (bg.opacity ?? 12) / 100,
      }}
    />
  );
}
